import type { AttributeField, NodeField } from "@/features/properties/types/propertyAttributes";
import { getSurveyChildren, isSurveyLeafQuestion, surveyLeafHasValue } from "@/shared/lib/surveyFieldValue";

export type FlaggedAsset = {
  id: string;
  name: string;
  missingData: boolean;
  missingPhoto: boolean;
};

export type AssetCompletenessRow = {
  categoryId: string;
  name: string;
  totalAssets: number;
  dataScore: number;
  photosScore: number;
  flaggedAssets: FlaggedAsset[];
};

export type PropertyAssetCompletenessSource = {
  id: string;
  name: string | null;
  category_id: string | null;
  manufacturer: string | null;
  model_number: string | null;
  nameplate_photo_url: string | null;
  deleted_at?: string | null;
  status?: string | null;
  asset_categories?: { name: string | null } | { name: string | null }[] | null;
};

export const VISIBLE_ASSET_COMPLETENESS_CATEGORIES = new Set([
  "Beverage Equipment",
  "Electrical",
  "Food Service Equipment",
  "HVAC",
  "Plumbing",
  "Refrigeration",
]);

export type UnansweredSurveyQuestion = {
  id: string;
  title: string;
};

export type SurveyCompletenessRow = {
  id: string;
  name: string;
  score: number;
  answeredQuestions: number;
  totalQuestions: number;
  unansweredQuestions: UnansweredSurveyQuestion[];
};

function countSurveyQuestions(fields: AttributeField[]): {
  answered: number;
  total: number;
  unanswered: UnansweredSurveyQuestion[];
} {
  return fields.reduce(
    (stats, field) => {
      // Recurse containers (node, or any grouping field with child `fields`).
      const children = getSurveyChildren(field);
      if (children) {
        const childStats = countSurveyQuestions(children as AttributeField[]);
        return {
          answered: stats.answered + childStats.answered,
          total: stats.total + childStats.total,
          unanswered: [...stats.unanswered, ...childStats.unanswered],
        };
      }

      // Skip value-less structural markers (e.g. an empty page/ellipsis container
      // that arrived with no children) — they aren't real questions.
      if (!isSurveyLeafQuestion(field)) {
        return stats;
      }

      // Leaf question. `surveyLeafHasValue` judges "answered" by the value shape,
      // so leaf types the AttributeField union omits (date, number, text, switch,
      // …) are counted correctly — a filled "Site Visit Date" (type "date") is no
      // longer flagged as missing.
      const answered = surveyLeafHasValue(field);
      return {
        answered: stats.answered + (answered ? 1 : 0),
        total: stats.total + 1,
        unanswered: answered
          ? stats.unanswered
          : [...stats.unanswered, { id: field.id, title: field.title }],
      };
    },
    { answered: 0, total: 0, unanswered: [] as UnansweredSurveyQuestion[] }
  );
}

export function getSurveyCompletenessRows(attributes: AttributeField[]): SurveyCompletenessRow[] {
  return attributes
    .filter((field): field is NodeField => field.type === "node")
    .map((section) => {
      const stats = countSurveyQuestions(section.fields);
      return {
        id: section.id,
        name: section.title,
        answeredQuestions: stats.answered,
        totalQuestions: stats.total,
        unansweredQuestions: stats.unanswered,
        score: stats.total > 0 ? Math.round((stats.answered / stats.total) * 100) : 0,
      };
    })
    .filter((section) => section.totalQuestions > 0);
}
